import type { CharacterDef } from '@md/engine';
import { type NetStatusSnapshot, PLAYER_INDICES, type PlayerIndex } from '@md/shared';
import { button, el } from '../ui/dom';

/**
 * Écran de CHOIX DU PERSONNAGE (concept §6.2) — avec **verrouillage mutuel** :
 * dès qu'un joueur prend Polo, la carte Polo est grisée et non cliquable chez
 * l'autre. Le verrou fait autorité côté serveur (`CHAR_TAKEN`) ; l'interface ne
 * fait que le refléter.
 */
export interface CharSelectScreen {
  readonly element: HTMLElement;
  update(status: NetStatusSnapshot): void;
}

export interface CharSelectScreenOptions {
  readonly characters: readonly [CharacterDef, CharacterDef];
  readonly onPick: (charIndex: PlayerIndex) => void;
  readonly onLeave: () => void;
}

function tintCss(tint: number): string {
  return `#${tint.toString(16).padStart(6, '0')}`;
}

export function createCharSelectScreen(options: CharSelectScreenOptions): CharSelectScreen {
  const view = el('main', 'screen screen--chars');
  view.append(el('h1', 'lobby__title', 'Qui joue qui ?'));

  const grid = el('div', 'chars');
  const cards = PLAYER_INDICES.map((index) => {
    const character = options.characters[index];
    const card = button(character.name, `char-${index}`, 'char-card');
    card.style.setProperty('--char-tint', tintCss(character.tint));
    card.dataset.charIndex = String(index);
    card.append(el('span', 'char-card__state'));
    card.addEventListener('click', () => options.onPick(index));
    grid.append(card);
    return card;
  });

  const hint = el('p', 'lobby__hint');
  hint.dataset.testid = 'chars-hint';

  const leave = button('Quitter la partie', 'chars-leave', 'button button--ghost');
  leave.addEventListener('click', () => options.onLeave());

  view.append(grid, hint, leave);

  return {
    element: view,

    update(status: NetStatusSnapshot): void {
      for (const index of PLAYER_INDICES) {
        const card = cards[index];
        if (!card) {
          continue;
        }
        const owner = status.players.find((player) => player.charIndex === index);
        const mine = owner?.memberId === status.memberId && owner !== undefined;
        const takenByOther = owner !== undefined && !mine;
        card.dataset.taken = String(takenByOther);
        card.dataset.mine = String(mine);
        card.disabled = takenByOther;
        const state = card.querySelector('.char-card__state');
        if (state) {
          state.textContent = mine ? 'Vous' : takenByOther ? 'Pris' : 'Libre';
        }
      }
      const chosen = status.charIndex !== null;
      const partner = status.players.find((player) => player.memberId !== status.memberId);
      const partnerChosen = partner !== undefined && partner.charIndex !== null;
      // Une place réservée par une déconnexion reste visible : sans ce message,
      // l'attente ressemblait à un écran figé.
      const partnerGone = partner !== undefined && !partner.connected;
      view.dataset.partnerConnected = String(partner?.connected ?? false);
      hint.textContent = partnerGone
        ? 'Votre partenaire s’est déconnecté — sa place lui est réservée, il peut revenir.'
        : !chosen
          ? 'Choisissez votre robot.'
          : partnerChosen
            ? 'Les deux robots sont choisis.'
            : 'En attente du choix de votre partenaire…';
    },
  };
}
